import { t } from '@lingui/macro';
import { i18n } from '@lingui/core';
import EntityServiceFactory, { ENTITY_TYPES } from '../../../services/EntityService';

export const AND = 'AND';
export const OR = 'OR';
export const title = t`Tags`;

export const TAG_OP_SUMMARIES = {
    [AND]: t`All`,
    [OR]: t`Any`,
};

export const ENTITY_TYPE_TITLES = {
    [ENTITY_TYPES.SITE]: t`Site`,
    [ENTITY_TYPES.BUSINESS_SERVICE]: t`Business Service`,
};

export const ENTITY_TYPE_TITLES_PLURAL = {
    [ENTITY_TYPES.SITE]: t`Sites`,
    [ENTITY_TYPES.BUSINESS_SERVICE]: t`Business Services`,
};

export const ENTITY_TYPE_MAPPING = {
    [ENTITY_TYPES.SITE]: 'site',
    [ENTITY_TYPES.BUSINESS_SERVICE]: 'businessservice',
};

export const FILTER_STATE_FIELDS = {
    [ENTITY_TYPES.SITE]: 'sites',
    [ENTITY_TYPES.BUSINESS_SERVICE]: 'businessServices',
};

export const ENTITY_LABEL_FIELDS = {
    [ENTITY_TYPES.SITE]: 'name',
    [ENTITY_TYPES.BUSINESS_SERVICE]: 'name',
};

export const ENTITY_ID_FIELDS = {
    [ENTITY_TYPES.SITE]: 'uuid',
    [ENTITY_TYPES.BUSINESS_SERVICE]: 'uuid',
};

export const FILTER_NAME = {
    [ENTITY_TYPES.SITE]: 'siteGroup',
    [ENTITY_TYPES.BUSINESS_SERVICE]: 'businessServiceGroup',
};

export default function getEntityTagGroupFilterServiceFactory(entityType) {
    return (filterId) => {
        const filterStateField = FILTER_STATE_FIELDS[entityType];
        const filterName = FILTER_NAME[entityType];

        const EntityTagGroupFilterService = {
            /*
             * Factory functions
             */

            createWithEncodedState(encodedState, dataService) {
                const state = EntityTagGroupFilterService.decode(encodedState);
                const uuid = state[filterStateField][0];
                if (!uuid) return { ...state, name: null };

                return EntityServiceFactory(dataService)
                    .getEntity(entityType, uuid)
                    .then((entity) => {
                        return { ...state, name: entity.name };
                    });
            },

            createWithDefaultState() {
                return {
                    tags: [],
                    tagOp: AND,
                    [filterStateField]: [],
                    name: null,
                };
            },

            createState(encodedFilterState, dataService) {
                const encodedState = encodedFilterState?.[filterId];
                return encodedState
                    ? this.createWithEncodedState(encodedState, dataService)
                    : this.createWithDefaultState(dataService);
            },

            /*
             * Transformation functions
             */

            parameterize(state) {
                const { tags, tagOp } = state;
                const entities = state[filterStateField];

                return {
                    [filterName]: EntityTagGroupFilterService.encode({
                        tags,
                        tagOp,
                        [filterStateField]: entities,
                    }),
                };
            },

            encode(state) {
                const { name, ...encodeStateFields } = state;
                return JSON.stringify(encodeStateFields);
            },

            decode(encodedState) {
                return JSON.parse(encodedState);
            },

            summarize(state) {
                const { tags, tagOp, name } = state;
                const entities = state[filterStateField];
                const tagOpText = i18n._(TAG_OP_SUMMARIES[tagOp]);
                const entityTypeText = i18n._(ENTITY_TYPE_TITLES_PLURAL[entityType]);

                const tagText =
                    tags.length > 1
                        ? `${tagOpText} ${i18n._(t`tags`)}: ${tags[0]} + ${tags.length - 1} more`
                        : `${i18n._(t`Tag`)}: ${tags[0]}`;

                const entityText =
                    entities.length > 1
                        ? `${entityTypeText}: ${name} + ${entities.length - 1} more`
                        : `${entityTypeText}: ${name}`;

                const summary = [entities.length > 0 && entityText, tags.length > 0 && tagText]
                    .filter((x) => !!x)
                    .join(', ');

                return {
                    title,
                    value: summary,
                    shortValue: summary,
                };
            },
        };

        return EntityTagGroupFilterService;
    };
}
